import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { api } from "../services/api.service";
import { shouldSkipApiCalls } from '../utils/routeUtils';
import { websocketService } from "../services/websocket.service";
import { toast } from "sonner";
import {
  Notification,
  NotificationType,
  CreateNotificationDto,
  GetNotificationsDto,
} from "../types/notification";

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  addNotification: (notification: CreateNotificationDto) => Promise<void>;
  markAsRead: (notificationIds: number[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  archiveNotifications: (notificationIds: number[]) => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  fetchNotifications: (params?: GetNotificationsDto) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  // Refresh unread count
  const refreshUnreadCount = useCallback(async () => {
    try {
      const response = await api.notifications.getUnreadCount();
      setUnreadCount(response.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    let isSubscribed = true;
    
    const initializeWebSocket = async () => {
      try {
        const user = localStorage.getItem("user");
        const userId = user ? JSON.parse(user).id : undefined;
        
        await websocketService.connect(userId);
        
        if (!isSubscribed) return;

        // Listen for real-time notifications
        const handleNotification = (notification: Notification) => {
          console.log('Received notification:', notification);
          setNotifications(prev => [...(prev || []), notification]);
          setUnreadCount(prev => prev + 1);
          
          // Show toast notification
          const toastType = notification.type === NotificationType.ERROR ? "error" 
            : notification.type === NotificationType.SUCCESS ? "success"
            : notification.type === NotificationType.WARNING ? "warning"
            : "info";
          
          toast[toastType](notification.title, {
            description: notification.message,
          });
        };

        // Listen for notification updates
        const handleNotificationRead = ({ notificationIds }: { notificationIds: number[] }) => {
          setNotifications(prev => 
            (prev || []).map(n => 
              notificationIds.includes(n.id) ? { ...n, isRead: true } : n
            )
          );
          refreshUnreadCount();
        };

        const handleNotificationArchived = ({ notificationIds }: { notificationIds: number[] }) => {
          setNotifications(prev => 
            (prev || []).filter(n => !notificationIds.includes(n.id))
          );
          refreshUnreadCount();
        };

        const handleNotificationDeleted = ({ notificationId }: { notificationId: number }) => {
          setNotifications(prev => 
            (prev || []).filter(n => n.id !== notificationId)
          );
          refreshUnreadCount();
        };

        websocketService.on("notification", handleNotification);
        websocketService.on("notification:read", handleNotificationRead);
        websocketService.on("notification:archived", handleNotificationArchived);
        websocketService.on("notification:deleted", handleNotificationDeleted);

        // Cleanup function to remove listeners
        return () => {
          websocketService.off("notification", handleNotification);
          websocketService.off("notification:read", handleNotificationRead);
          websocketService.off("notification:archived", handleNotificationArchived);
          websocketService.off("notification:deleted", handleNotificationDeleted);
        };
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
      }
    };

    initializeWebSocket();

    return () => {
      isSubscribed = false;
      // Only disconnect if no other components are using the connection
      if (websocketService.isConnected()) {
        websocketService.disconnect();
      }
    };
  }, [refreshUnreadCount]);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async (params?: GetNotificationsDto) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.notifications.getAll(params);
      console.log('Raw API response:', response);
      
      // Handle the actual backend response format
      if (response.data && response.meta) {
        setNotifications(response.data);
        setTotalPages(response.meta.totalPages);
        setCurrentPage(parseInt(response.meta.page));
      } else {
        // Fallback for the expected format
        setNotifications(response.notifications || []);
        setTotalPages(response.totalPages || 1);
        setCurrentPage(response.page || 1);
      }
    } catch (error: any) {
      console.error("Error fetching notifications:", error);
      setError(error.message || "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Add notification (for admin use)
  const addNotification = async (notification: CreateNotificationDto) => {
    try {
      await api.notifications.create(notification);
      // WebSocket will handle adding it to the list
      toast.success("Notification sent successfully");
    } catch (error: any) {
      console.error("Error creating notification:", error);
      toast.error(error.message || "Failed to create notification");
      throw error;
    }
  };

  // Mark notifications as read
  const markAsRead = async (notificationIds: number[]) => {
    try {
      await api.notifications.markAsRead(notificationIds);
      // WebSocket will handle updating the state
    } catch (error: any) {
      console.error("Error marking as read:", error);
      toast.error(error.message || "Failed to mark notifications as read");
      throw error;
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await api.notifications.markAllAsRead();
      setNotifications(prev => (prev || []).map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error: any) {
      console.error("Error marking all as read:", error);
      toast.error(error.message || "Failed to mark all notifications as read");
      throw error;
    }
  };

  // Archive notifications
  const archiveNotifications = async (notificationIds: number[]) => {
    try {
      await api.notifications.archive(notificationIds);
      // WebSocket will handle removing from the list
    } catch (error: any) {
      console.error("Error archiving notifications:", error);
      toast.error(error.message || "Failed to archive notifications");
      throw error;
    }
  };

  // Delete notification
  const deleteNotification = async (id: number) => {
    try {
      await api.notifications.delete(id);
      // WebSocket will handle removing from the list
    } catch (error: any) {
      console.error("Error deleting notification:", error);
      toast.error(error.message || "Failed to delete notification");
      throw error;
    }
  };

  // Load initial notifications and unread count (skip on public pages)
  useEffect(() => {
    // Skip notifications fetch on public pages to avoid unnecessary API calls
    if (!shouldSkipApiCalls()) {
      fetchNotifications({ isArchived: false, limit: 20 });
      refreshUnreadCount();
    }
  }, [fetchNotifications, refreshUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        notifications: notifications || [],
        unreadCount,
        loading,
        error,
        totalPages,
        currentPage,
        addNotification,
        markAsRead,
        markAllAsRead,
        archiveNotifications,
        deleteNotification,
        fetchNotifications,
        refreshUnreadCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};